// Client.js
// This is the client details view.

import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { ActivityIndicator, Image, View, Text } from "react-native";
import { Feather } from "@react-native-vector-icons/feather";
import { faCircleUser } from "@fortawesome/free-regular-svg-icons";
import { faPaw, faUser, faUsers, faTriangleExclamation, faStar, faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";

import { deepCopy, stateReducer } from "components/helpers/stateReducer";
import { FontAwesomeIcon } from "@fortawesome/react-native-fontawesome";
import { Menu, MenuOptions, MenuOption, MenuTrigger } from "react-native-popup-menu";  // https://www.npmjs.com/package/react-native-popup-menu

import Anchor from "components/Partials/Anchor";
import * as SideScreens from "components/Views/SideScreens/Index";
import { openSideScreen } from "components/Views/SideScreens/SideScreenLoader";
import { UpcomingAppointments } from "components/Views/SideScreens/Partials";
import { InvoiceRow } from "components/Views/SideScreens/InvoicesList";


const ClientDetails = ({client_id}) => {
  const _theme = window.main.theme;

  if (!Number(client_id)) {
    return (
      <View id="patient_details_invalid" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.caution,fontSize:18,lineHeight:22}}>Client Details: Invalid client.</Text>
      </View>
    );
  }

  const [clientViewState, clientViewSetState] = useReducer(stateReducer, {
    clientDetails: {},
    clientLoading: true,  // ⬅ Default TRUE for iniital 'loading' state.
  });
  const _clientDetailsId = useRef(0);

  const initialDataLoad = useCallback(async () => {
    if (!!Number(_clientDetailsId.current)) {
      return false;
    }
    _clientDetailsId.current = client_id;
    const _stateProps = [
      { key: "clientLoading", type: "boolean", clientLoading: false }
    ];
    try {
      const [_clientDetails] = await Promise.all([
        window.main.axios.get(window.main.api.getClientDetails.replace("{client_id}",client_id)),
      ]);
      if (!_clientDetails || !_clientDetails.data || !Number(_clientDetails.data.id)) {
        throw _clientDetails;
      }
      _stateProps.push({ key: "clientDetails", type: "object", clientDetails: _clientDetails.data });
      // Update all states at once:
      clientViewSetState(_stateProps);
    } catch (err) {
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      clientViewSetState(_stateProps);
    }
  }, []);

  useEffect(() => {
    initialDataLoad();
  }, []);  // ⬅ Trigger only once on initial page load.

  if (!clientViewState.clientLoading && !Number(clientViewState.clientDetails.id)) {
    return (
      <View id="client_details_error" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.caution,fontSize:18,lineHeight:22}}>Client Details failed to load.</Text>
      </View>
    );
  }

  return (
    <>

      {!!Number(clientViewState.clientDetails.id) &&
        <View id="client_details__sidescreen" style={_theme.tiles.outerContainer}>

          {/* Client Profile */}
          <View id="client_profile" style={_theme.tileRowNested}>

            <AppointmentSubmenu menuData={{client_id:clientViewState.clientDetails.id}} />

            <View id="client_profile_image" style={_theme.containerRow}>
              {!!Number(clientViewState.clientDetails.profile_image_id) &&
                <Image source={{ uri:`${window.main.api.url}/api/documents/download/${clientViewState.clientDetails.id}/${clientViewState.clientDetails.profile_image_id}` }} style={{objectFit:"cover",height:100,width:100,marginRight:20}} />
              }
              {!Number(clientViewState.clientDetails.profile_image_id) &&
                <FontAwesomeIcon icon={faCircleUser} style={{color:_theme.colors.mode.containerBackgroundColor4,height:100,width:100,marginRight:20}} />
              }
              <View style={_theme.tiles.seactionHeader}>
                <Text style={_theme.tiles.sectionHeaderKey}>Client:</Text>
                <Text style={_theme.tiles.sectionHeaderTitle}>{clientViewState.clientDetails.title}</Text>
              </View>
            </View>
            <View id="client_profile_fields" style={{..._theme.tiles.fieldsColumn}}>
              {/* Custom Fields */}
              {!!clientViewState.clientDetails.fields && !!clientViewState.clientDetails.fields.length && clientViewState.clientDetails.fields.map((fieldObj)=>{
                if (!fieldObj.value) {
                  return null;
                }
                return (
                  <View key={"fieldObj_"+fieldObj.field_id} style={_theme.tiles.fieldRow}>
                    <Text style={_theme.tiles.fieldRowLabel}>{fieldObj.title}</Text>
                    <Text style={_theme.tiles.fieldRowValue}>
                      {fieldObj.type==="date" && <><Text style={{whiteSpace:"nowrap"}}>{window.main.lib.dateFormat(fieldObj.value.replace(/Z$/,""))}</Text>{"\n"}<Text style={{fontSize:14,whiteSpace:"nowrap"}}>({window.main.lib.sinceDate(fieldObj.value.replace(/Z$/,""))})</Text></>}
                      {fieldObj.type==="textarea" && <Text style={{whiteSpace:"pre-wrap"}}>{fieldObj.value}</Text>}
                      {fieldObj.type!=="textarea" && fieldObj.type!=="date" && <Text style={{}}>{fieldObj.value}</Text>}
                    </Text>
                  </View>
                );
              })}
              {/* Regular fields */}
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>PayJunction ID</Text>
                <Text style={_theme.tiles.fieldRowValue}>{clientViewState.clientDetails.pj_client_id || "N/A"}</Text>
              </View>
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>Created</Text>
                <Text style={_theme.tiles.fieldRowValue}><Text style={{whiteSpace:"nowrap"}}>{window.main.lib.dateFormat(clientViewState.clientDetails.created_date.replace(/Z$/,""))}</Text>{"\n"}<Text style={{fontSize:14,whiteSpace:"nowrap"}}>({window.main.lib.sinceDate(clientViewState.clientDetails.created_date.replace(/Z$/,""))})</Text></Text>
              </View>
              <View style={_theme.tiles.fieldRow}>
                <Text style={_theme.tiles.fieldRowLabel}>Updated</Text>
                <Text style={_theme.tiles.fieldRowValue}><Text style={{whiteSpace:"nowrap"}}>{window.main.lib.dateTimeFormat(clientViewState.clientDetails.updated_date.replace(/Z$/,""))}</Text>{"\n"}<Text style={{fontSize:14,whiteSpace:"nowrap"}}>({window.main.lib.sinceDate(clientViewState.clientDetails.updated_date.replace(/Z$/,""))})</Text></Text>
              </View>
              <View style={_theme.tiles.fieldRow}>
                  <Text style={_theme.tiles.fieldRowLabel}>Tags {/*({clientViewState.clientDetails.tags.length})*/}</Text>
                  <View style={{..._theme.tiles.fieldRowValue,flexDirection:"row"}}>
                    {(!clientViewState.clientDetails.tags || !clientViewState.clientDetails.tags.length) &&
                      <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No tags.</Text>
                    }
                    {!!clientViewState.clientDetails.tags && !!clientViewState.clientDetails.tags.length && clientViewState.clientDetails.tags.map((tagObj)=>{
                      return (
                        <Text key={"tagObj_"+tagObj.id} style={{..._theme.tileChip}}>{tagObj.tag}</Text>
                      );
                    })}
                  </View>
              </View>
            </View>
          </View>

          {/* Patients List */}
          <View id="client_patients" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Patients</Text>
            {(!clientViewState.clientDetails.patients || !clientViewState.clientDetails.patients.length) &&
              <View id="client_patient_row" style={{..._theme.tiles.fieldRow,alignItems:"center",alignSelf:"stretch"}}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No patients.</Text>
              </View>
            }
            {!!clientViewState.clientDetails.patients && !!clientViewState.clientDetails.patients.length && clientViewState.clientDetails.patients.map((patientObj)=>{
              const openPatientSideScreen = ()=>{ openSideScreen("patient", {client_id:patientObj.client_id, patient_id:patientObj.id}); };
              return (
                <Anchor key={"patientObj_"+patientObj.id} id={"client_patient_row_"+patientObj.id} style={{..._theme.tiles.fieldRow,alignItems:"flex-start",alignSelf:"stretch"}} onPress={openPatientSideScreen}>
                  <View style={{..._theme.containerRow,flexShrink:1,alignItems:"center",color:"inherit"}}>
                    {!!Number(patientObj.profile_image_id) &&
                      <Image source={{ uri:`${window.main.api.url}/api/documents/download/${clientViewState.clientDetails.id}/${patientObj.profile_image_id}` }} style={{height:30,width:30,objectFit:"cover",borderRadius:"50%",marginRight:10}} />
                    }
                    {!Number(patientObj.profile_image_id) &&
                      <FontAwesomeIcon icon={faPaw} style={{color:_theme.colors.mode.containerBackgroundColor4,height:24,width:24,minWidth:24,padding:2,borderWidth:3,borderColor:_theme.colors.mode.containerBackgroundColor4,borderRadius:"50%",borderStyle:"solid",marginTop:-2,marginBottom:-2,marginRight:10}} />
                    }
                    <Text style={{display:"inline-block",maxWidth:"65%",fontSize:16,color:"inherit"}}>
                      {patientObj.title}
                      {!!patientObj.has_memberships && <FontAwesomeIcon icon={faUsers} style={{color:_theme.colors.warn,height:14,width:14,marginLeft:5}} />}
                      {!!patientObj.alerts && <FontAwesomeIcon icon={faTriangleExclamation} style={{color:_theme.colors.caution,height:14,width:14,marginLeft:5}} />}
                    </Text>
                  </View>
                  <View style={{..._theme.containerRow,paddingRight:2}}>
                    <Text style={{..._theme.tiles.fieldRowValue,justifyContent:"center",alignItems:"flex-end",textAlign:"right"}}>
                      <Text style={{whiteSpace:"nowrap"}}>{window.main.lib.sinceDate(patientObj.birth_date).replace("ago","old")}</Text>{"\n"}{patientObj.breed_name}{!!patientObj.second_breed_name&&patientObj.second_breed_name.trim()!==""&&patientObj.second_breed_name!=="<Please Select>" ? "/"+patientObj.second_breed_name : ""}
                    </Text>
                  </View>
                </Anchor>
              );
            })}
          </View>

          {/* Client Contacts */}
          <View id="client_contacts" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Contacts</Text>
            {(!clientViewState.clientDetails.contacts || !clientViewState.clientDetails.contacts.length) &&
              <View id="client_contacts_row" style={{..._theme.tiles.fieldRow,flexDirection:"column",alignSelf:"stretch"}}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No contacts.</Text>
              </View>
            }
            {!!clientViewState.clientDetails.contacts && !!clientViewState.clientDetails.contacts.length && clientViewState.clientDetails.contacts.map((contactObj)=>{
              return (
                <View key={"contactObj_"+contactObj.id} id={"client_contacts_row_"+contactObj.id} style={{..._theme.tiles.fieldRow,flexDirection:"column",alignSelf:"stretch"}}>
                  <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
                    {contactObj.first_name+" "+contactObj.last_name}
                    {!!contactObj.description && "  ("+contactObj.description+")"}
                    {!!contactObj.primary && <FontAwesomeIcon icon={faStar} style={{color:_theme.colors.success,height:14,width:14,marginLeft:8}} />}
                  </Text>
                  {!!contactObj.phone_1 &&
                    <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",whiteSpace:"nowrap",paddingTop:5}}>
                      <FontAwesomeIcon icon={faPhone} style={{color:_theme.colors.mode.textAccent,height:13,width:13,marginLeft:2,paddingTop:1,paddingRight:8}} />
                      {window.main.lib.phoneFormat(contactObj.phone_1)}
                    </Text>
                  }
                  {!!contactObj.email_1 &&
                    <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",whiteSpace:"nowrap",paddingTop:5}}>
                      <FontAwesomeIcon icon={faEnvelope} style={{color:_theme.colors.mode.textAccent,height:14,width:14,marginLeft:1,paddingTop:2,paddingRight:8}} />
                      {window.main.lib.phoneFormat(contactObj.email_1)}
                    </Text>
                  }
                </View>
              );
            })}
          </View>

          {/* Client Locations */}
          <View id="client_locations" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Addresses</Text>
            {(!clientViewState.clientDetails.locations || !clientViewState.clientDetails.locations.length) &&
              <View id="client_locations_row" style={{..._theme.tiles.fieldRow,flexDirection:"column",alignSelf:"stretch"}}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No addresses.</Text>
              </View>
            }
            {!!clientViewState.clientDetails.locations && !!clientViewState.clientDetails.locations.length && clientViewState.clientDetails.locations.map((locObj)=>{
              return (
                <View key={"locObj_"+locObj.id} id={"client_location_row_"+locObj.id} style={{..._theme.tiles.fieldRow,flexDirection:"column",alignSelf:"stretch"}}>
                  <Text style={{..._theme.tiles.fieldRowLabel,flexGrow:1,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
                    {locObj.title}
                    {!!locObj.primary && <FontAwesomeIcon icon={faStar} style={{color:_theme.colors.success,height:14,width:14,marginLeft:10}} />}
                  </Text>
                  {(!!locObj.address_1 || !!locObj.address_2) &&
                    <Text style={{..._theme.tiles.fieldRowValue,flexGrow:1,flexDirection:"row",whiteSpace:"nowrap",paddingTop:5}}>
                      {locObj.address_1}{(!!locObj.address_1?" ":"")+locObj.address_2}
                    </Text>
                  }
                  {(!!locObj.city || !!locObj.state || !!locObj.zip) &&
                    <Text style={{..._theme.tiles.fieldRowValue,flexGrow:1,flexDirection:"row",whiteSpace:"nowrap"}}>
                      {!!locObj.city?locObj.city+", ":""}{!!locObj.state?locObj.state+" ":""}{locObj.zip}
                    </Text>
                  }
                </View>
              );
            })}
          </View>

          {/* Client Appointments */}
          <UpcomingAppointments appointments={clientViewState.clientDetails.appointments} />

          {/* Client Open Invoices */}
          <View id="client_invoices" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Open Invoices</Text>
            {(!clientViewState.clientDetails.invoices || !clientViewState.clientDetails.invoices.length) &&
              <View id="client_invoices_row" style={{..._theme.tiles.fieldRow,flexDirection:"column",alignSelf:"stretch"}}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No open invoices.</Text>
              </View>
            }
            {!!clientViewState.clientDetails.invoices && !!clientViewState.clientDetails.invoices.length && clientViewState.clientDetails.invoices.map((invoiceObj)=>{
              return (
                <InvoiceRow key={"invoiceObj_"+invoiceObj.id} invoiceObj={invoiceObj} />
              );
            })}
          </View>

        </View>
      }

      {!window.main.lib.getLoadingState() && !!clientViewState.clientLoading &&
        // <View style={{zIndex:99999, backgroundColor:"rgba(255,255,255,0.7)", top:0, left:0, bottom:0, right:0, position:(window.main.platform_os==="web"?"fixed":"absolute")}}>
        <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>
          <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:20}} />
        </View>
      }

    </>
  );
};

const AppointmentSubmenu = ({menuData})=>{
  const _theme = window.main.theme;
  const _menuRef = useRef();
  const menuSideScreens = {};
  // if (!!SideScreens.client && !!SideScreens.client.key) {
  //   menuSideScreens.client = deepCopy(SideScreens.client);
  //   menuSideScreens.client.onPress = () => {
  //     openSideScreen("client", {client_id:menuData.client_id});  // client_id must always exist for layer-validation
  //     _menuRef.current?.close();
  //   }
  // };
  // if (!!SideScreens.patient && !!SideScreens.patient.key) {
  //   menuSideScreens.patient = deepCopy(SideScreens.patient);
  //   menuSideScreens.patient.onPress = () => {
  //     openSideScreen("patient", {client_id:menuData.client_id, patient_id:menuData.patient_id});  // client_id must always exist for layer-validation
  //     _menuRef.current?.close();
  //   }
  // };
  // if (!!SideScreens.checkin && !!SideScreens.checkin.key && !Number(menuData.visit_id)) {
  //   menuSideScreens.checkin = deepCopy(SideScreens.checkin);
  //   menuSideScreens.checkin.onPress = () => {
  //     openSideScreen("checkin", {client_id:menuData.client_id, apptObj:menuData});  // client_id must always exist for layer-validation
  //     _menuRef.current?.close();
  //   }
  // };
  // if (!!SideScreens.visit && !!SideScreens.visit.key && !!Number(menuData.visit_id)) {
  //   menuSideScreens.visit = deepCopy(SideScreens.visit);
  //   menuSideScreens.visit.onPress = () => {
  //     openSideScreen("visit", {client_id:menuData.client_id, patient_id:menuData.patient_id, visit_id:menuData.visit_id});  // client_id must always exist for layer-validation
  //     _menuRef.current?.close();
  //   }
  // };
  // if (!!SideScreens.assessment && !!SideScreens.assessment.key && !!Number(menuData.assessment_id)) {
  //   menuSideScreens.assessment = deepCopy(SideScreens.assessment);
  //   menuSideScreens.assessment.onPress = () => {
  //     openSideScreen("assessment", {client_id:menuData.client_id, apptObj:menuData});  // client_id must always exist for layer-validation
  //     _menuRef.current?.close();
  //   }
  // };
  if (!!SideScreens.billing && !!SideScreens.billing.key) {
    menuSideScreens.billing = deepCopy(SideScreens.billing);
    menuSideScreens.billing.onPress = () => {
      openSideScreen("billing", {client_id:menuData.client_id});  // client_id must always exist for layer-validation
      _menuRef.current?.close();
    }
  };

  return (
    <Menu ref={_menuRef} style={{position:"absolute",top:8,right:8,marginHorizontal:0,zIndex:1010}}>
      <MenuTrigger customStyles={_theme.rnpmMenuTrigger}><Feather name="more-vertical" style={{..._theme.moreMenuButton,..._theme.moreMenuButtonIcon,fontSize:22,lineHeight:30,textAlign:"center"}} /></MenuTrigger>
      <MenuOptions customStyles={_theme.rnpmMenuOptions}>
        {Object.keys(menuSideScreens).map((key)=>{
          const viewObj = menuSideScreens[key];
          const RenderIcon = (()=>{
            if (!!viewObj.iconSource && viewObj.iconSource==="fa" && typeof(viewObj.icon)==="object") {
              return (<FontAwesomeIcon icon={viewObj.icon} style={{color:_theme.colors.mode.textColor4,height:14,width:14,marginBottom:1,marginRight:6,marginLeft:0}} />);
            }  // Else:
            return (<Feather name={viewObj.icon} size={14} style={{color:_theme.colors.mode.textColor4,marginBottom:1,marginRight:6,marginLeft:1}} />);
          })();  // ⬅ Immediate evaluation before passing into 'MoreMenuItem'.
          return (
            <MenuOption key={viewObj.title} id={`moremenu_${key}`} onPress={viewObj.onPress} customStyles={_theme.rnpmMenuOption}>
              <Text style={_theme.rnpmMenuOptionText}>
                {!!viewObj.iconSource && viewObj.iconSource==="fa" && typeof(viewObj.icon)==="object" &&
                  <FontAwesomeIcon icon={viewObj.icon} style={_theme.rnpmMenuOptionIcon} />
                }
                {!(!!viewObj.iconSource && viewObj.iconSource==="fa" && typeof(viewObj.icon)==="object") && !!viewObj.icon &&
                  <Feather name={viewObj.icon} size={_theme._theme.rnpmMenuOptionIcon.fontSize} style={{..._theme._theme.rnpmMenuOptionIcon,marginLeft:1}} />
                }
                {viewObj.title}
              </Text>
            </MenuOption>
          );
        })}
      </MenuOptions>
    </Menu>
  );
};

// Exports

const config = {
  key: "client",
  title: "Client Details",
  view: ClientDetails,
  menu: "sidescreen",
  iconSource: "fa",
  icon: faUser,
};

export default ClientDetails;
export {
  config,
};
